import React from 'react';
import { FunctionComponent, PropsWithChildren } from 'react';
import { HiMiniArrowTopRightOnSquare } from 'react-icons/hi2';
import { IoNotificationsOffOutline } from 'react-icons/io5';
import { LuPaperclip } from 'react-icons/lu';
import { InternetAddress, Notification } from '~/libs/infrastructure/schemas/notifications';

export const DetailRow: FunctionComponent<
	PropsWithChildren<{
		title: string;
	}>
> = ({ children, title }) => (
	<div className='flex justify-start items-start px-2 py-1.5'>
		<dt className='grow-0 shrink-0 w-32 text-md leading-6 font-semibold text-cPurple-500 tracking-wide'>{title}</dt>
		<dd className='text-md leading-6 text-gray-800'>{children}</dd>
	</div>
);

DetailRow.displayName = 'Application.Notification.DetailRow';

export const InternetAddressHandle: React.FunctionComponent<{
	address: InternetAddress;
}> = ({ address }) => (
	<div>{address.personal !== null ? `${address.personal} <${address.emailAddress}>` : `${address.emailAddress}`}</div>
);

InternetAddressHandle.displayName = 'Application.Notification.InternetAddressHandle';

interface ExpandedFeedProps {
	notification: Notification;
}

export function ExpandedFeed({ notification }: ExpandedFeedProps) {
	const { email } = notification;
	return (
		<div className='pl-10 pr-2 pb-4'>
			{email == null && (
				<div className='py-8'>
					<div className='flex items-center justify-center py-2 w-full rounded text-gray-800 font-semibold text-md bg-white text-center shadow'>
						<IoNotificationsOffOutline className='relative -mt-px w-6 h-6 mr-1' /> No email details available
					</div>
				</div>
			)}

			{email !== null && (
				<>
					<dl className='divide-y divide-gray-100'>
						<DetailRow title='Sender:'>
							<InternetAddressHandle address={email.from} />
						</DetailRow>
						<DetailRow title='Recipient:'>
							{email.to.map((recipient, id) => (
								<InternetAddressHandle address={recipient} key={id} />
							))}
						</DetailRow>
						{email.cc.length > 0 ? (
							<DetailRow title='Cc:'>
								{email.cc.map((recipient, id) => (
									<InternetAddressHandle address={recipient} key={id} />
								))}
							</DetailRow>
						) : null}
						{notification.perex !== null ? (
							<DetailRow title='Content:'>
								<p className='max-w-4xl leading-6 line-clamp-3'>{notification.perex}</p>
							</DetailRow>
						) : null}
						{email.attachments.length > 0 ? (
							<DetailRow title='Attachments:'>
								<ul className='mb-1.5 pt-0.5' role='list'>
									{email.attachments.map((attachment) => (
										<li className='pb-1 flex items-center justify-between text-sm' key={attachment.id}>
											<a
												className='inline-flex justify-start items-center max-w-full font-semibold text-cPurple-500 underline underline-offset-2 truncate hover:text-cPurple-900 hover:underline'
												href={`/notifications/${notification.id}/emails/${email.id}/attachments/${attachment.id}/download`}
												target='_blank'
												rel='noreferrer'
											>
												<LuPaperclip className='w-4 h-4 mr-1.5' />
												{attachment.fileName}
											</a>
										</li>
									))}
								</ul>
							</DetailRow>
						) : null}
					</dl>
					<div className='pl-32'>
						<a
							className='mx-2 inline-flex justify-between items-center my-1 pl-3 pr-2.5 py-1.5 rounded md:text-label text-labelsmall md:leading-tight leading-tight font-display font-semibold text-cPurple-500 bg-cPurple-400 bg-opacity-20 tracking-wide transition hover:text-white hover:bg-opacity-100'
							href={`/notifications/email/detail?${new URLSearchParams([
								['notificationId', notification.id],
								['emailNotificationId', email.id],
							]).toString()}`}
							target='_blank'
							rel='noreferrer'
						>
							Show content
							<HiMiniArrowTopRightOnSquare className='ml-1.5 h-4 w-4' />
						</a>
					</div>
				</>
			)}
		</div>
	);
}
